import {
  Button,
  Flex,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useMemo } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import {
  CheckboxFormField,
  FormFieldLabel,
  InputFormField,
} from 'packages/common-frontend/components/FormField';
import { Select } from 'packages/common-frontend/components/Select';
import { BasePropertyParams } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/Property';

const formatLinkedAccountSelectOptions = (
  entity: CreditAgreement.DomainEntity.Items.BankAccountEntity,
): { value: string; label: string } => {
  const {
    entity: { name: accountName, accountNumber },
    type: { value: accountType },
  } = entity;

  const type = commonUtils.toSentenceCase(accountType);
  const maskedAccountNumber = commonUtils.maskInfo(accountNumber);

  return {
    value: entity.entity.id,
    label: `${type}: ${accountName}, acct: ${maskedAccountNumber}`,
  };
};

type EditLinkedAccountModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bankAccountsById: Map<
    string,
    CreditAgreement.DomainEntity.Items.BankAccountEntity
  >;
  basePropertyParams: BasePropertyParams;
  property: CreditAgreement.DomainEntity.BaseProperty;
};
export const EditLinkedAccountModal = ({
  isOpen,
  setIsOpen,
  bankAccountsById,
  basePropertyParams,
  property,
}: EditLinkedAccountModalProps) => {
  const {
    wipConfig,
    basePropertyKey,
    fullPropertyKey,
    propertyKey,
  } = basePropertyParams;
  const { section, value, _isWrittenIn_ } = property;

  finleyAssert(wipConfig, 'Expected wipConfig to exist');
  const { formProps } = wipConfig;

  const component:
    | CreditAgreement.DomainEntity.FundingRequestComponentBaseProperties
    | undefined = getIn(wipConfig?.formProps.values, `${basePropertyKey}`);

  const currentAccountId: string | undefined = getIn(
    wipConfig?.formProps.values,
    `${fullPropertyKey}.value`,
  );

  const contents:
    | CreditAgreement.DomainEntity.LinkedAccountProperty[]
    | undefined = getIn(
    wipConfig?.formProps.values,
    `${basePropertyKey}.${propertyKey}.contents`,
  );

  const usedAccountIds = [
    ...(component?.destinationAccounts?.contents ?? []),
    ...(component?.sourceAccounts?.contents ?? []),
  ]
    .map((account) => account.value)
    // keep the current selected account, so it is still available in the dropdown
    .filter((id) => id !== currentAccountId);

  const firstAccountId = contents?.find((account) =>
    commonUtils.uuidRegex.test(account.value),
  )?.value;
  const firstAccountType: Bank.AccountType | undefined = firstAccountId
    ? bankAccountsById.get(firstAccountId)?.type.value
    : undefined;
  const shouldFilterByAccountType =
    (contents?.length ?? 0) > 1 && !!firstAccountType;

  const selectOptions = useMemo(() => {
    const usedAccountIdsSet = new Set(usedAccountIds);
    return (
      Array.from(bankAccountsById.values())
        // filter out bank accounts already selected within the same funding request component
        // filter out bank accounts of different Bank.AccountType if multiple accounts are added
        .filter(
          (account) =>
            !usedAccountIdsSet.has(account.entity.id) &&
            (!shouldFilterByAccountType ||
              account.type.value === firstAccountType),
        )
        .map(formatLinkedAccountSelectOptions)
    );
  }, [
    bankAccountsById,
    usedAccountIds,
    firstAccountType,
    shouldFilterByAccountType,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="4xl">
        <ModalHeader>Edit Linked Account</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex alignItems="flex-end" flexDirection="row" mt={5}>
            <FormFieldLabel label="Linked Account" labelWidth="60" />
            <InputGroup width="100%">
              <Select
                value={value}
                onChange={(val) => {
                  formProps.setFieldValue(`${fullPropertyKey}.value`, val);
                }}
                options={selectOptions}
                width="xl"
              />
            </InputGroup>
          </Flex>
          <InputFormField
            propName={`${fullPropertyKey}.section`}
            label="Section"
            formProps={formProps}
            value={section}
            labelWidth="60"
            inputWidth="xl"
            flexDirection="row"
            alignItems="flex-end"
            marginTop={5}
          />
          <CheckboxFormField
            propName={`${fullPropertyKey}._isWrittenIn_`}
            label="Is Written In"
            formProps={formProps}
            value={_isWrittenIn_}
            labelWidth="60"
            inputWidth="sm"
            flexDirection="row"
            marginTop={5}
          />

          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                wipConfig.saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: formProps.values.id,
                      creditFacilityId: formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(formProps.values),
                    },
                  },
                });
                setIsOpen(false);
              }}
              aria-label="Done"
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
